import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RecognitionSection = () => {
  const awards = [
    {
      id: 1,
      title: "Best Digital Agency 2023",
      organization: "Delhi Business Awards",
      year: "2023",
      description: "Recognized for outstanding digital transformation services and client satisfaction",
      icon: "Award",
      image: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80"
    },
    {
      id: 2,
      title: "Innovation in Web Design",
      organization: "Indian Web Design Awards",
      year: "2023",
      description: "Honored for creative excellence and user experience innovation",
      icon: "Palette",
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80"
    },
    {
      id: 3,
      title: "Top Mobile App Developer",
      organization: "Tech Excellence Awards",
      year: "2022",
      description: "Recognized for exceptional mobile application development and user engagement",
      icon: "Smartphone",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80"
    },
    {
      id: 4,
      title: "Client Choice Award",
      organization: "Digital Services Review",
      year: "2022",
      description: "Voted by clients for exceptional service quality and project delivery",
      icon: "Heart",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80"
    }
  ];

  const certifications = [
    {
      name: "Google Partner",
      description: "Certified Google Ads and Analytics partner",
      icon: "Search",
      color: "primary"
    },
    {
      name: "AWS Solutions Architect",
      description: "Cloud architecture and deployment expertise",
      icon: "Cloud",
      color: "accent"
    },
    {
      name: "Meta Business Partner",
      description: "Facebook and Instagram marketing certified",
      icon: "Facebook",
      color: "trust"
    },
    {
      name: "Shopify Partner",
      description: "E-commerce development and optimization",
      icon: "ShoppingCart",
      color: "success"
    },
    {
      name: "HubSpot Certified",
      description: "Inbound marketing and CRM expertise",
      icon: "Target",
      color: "warning"
    },
    {
      name: "Adobe Certified Expert",
      description: "Creative design and multimedia production",
      icon: "Palette",
      color: "conversion"
    }
  ];

  const mediaFeatures = [
    {
      id: 1,
      publication: "TechCrunch India",
      title: "Rising Stars in Digital Transformation",
      date: "March 2023",
      excerpt: "Singh Creations is making waves in the digital agency space with their unique approach to combining creativity with technical excellence.",
      link: "#",
      image: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80"
    },
    {
      id: 2,
      publication: "Business Today",
      title: "Small Agencies, Big Impact",
      date: "January 2023",
      excerpt: "How boutique digital agencies like Singh Creations are outperforming larger competitors through personalized service and innovation.",
      link: "#",
      image: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80"
    },
    {
      id: 3,
      publication: "Entrepreneur India",
      title: "The Future of Digital Agencies",
      date: "November 2022",
      excerpt: "Singh Creations founder Jasmeet Singh shares insights on building a successful digital agency in the post-pandemic world.",
      link: "#",
      image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80"
    }
  ];

  const stats = [
    { number: "15+", label: "Industry Awards", icon: "Trophy" },
    { number: "25+", label: "Certifications", icon: "Award" },
    { number: "10+", label: "Media Features", icon: "Newspaper" },
    { number: "98%", label: "Client Satisfaction", icon: "Heart" }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-success rounded-lg flex items-center justify-center">
              <Icon name="Trophy" size={24} className="text-white" />
            </div>
            <span className="text-success font-semibold text-lg">Recognition</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Industry Recognition & Trust
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our commitment to excellence has been recognized by industry leaders, clients, and media outlets worldwide.
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {stats?.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-6 text-center brand-shadow brand-transition brand-hover-lift">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={stat?.icon} size={24} className="text-accent" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">{stat?.number}</div>
              <div className="text-muted-foreground">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Awards Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">Awards & Achievements</h3>
            <p className="text-lg text-muted-foreground">
              Recognition for our outstanding work and commitment to client success
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {awards?.map((award) => (
              <div key={award?.id} className="bg-white rounded-2xl p-6 brand-shadow brand-transition brand-hover-lift">
                <div className="flex items-start space-x-4">
                  <div className="relative">
                    <Image
                      src={award?.image}
                      alt={`${award?.title} award ceremony`}
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                      <Icon name={award?.icon} size={16} className="text-white" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="text-lg font-bold text-foreground">{award?.title}</h4>
                      <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">{award?.year}</span>
                    </div>
                    <p className="text-primary font-semibold mb-2">{award?.organization}</p>
                    <p className="text-sm text-muted-foreground">{award?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">Professional Certifications</h3>
            <p className="text-lg text-muted-foreground">
              Our team maintains cutting-edge expertise through continuous certification
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications?.map((cert, index) => (
              <div key={index} className="bg-white rounded-xl p-6 brand-shadow brand-transition brand-hover-lift">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 bg-${cert?.color}/10 rounded-lg flex items-center justify-center`}>
                    <Icon name={cert?.icon} size={24} className={`text-${cert?.color}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-foreground mb-1">{cert?.name}</h4>
                    <p className="text-sm text-muted-foreground">{cert?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Media Features */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">Media Coverage</h3>
            <p className="text-lg text-muted-foreground">
              Featured in leading publications for our innovative approach and industry insights
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {mediaFeatures?.map((feature) => (
              <div key={feature?.id} className="bg-white rounded-2xl overflow-hidden brand-shadow brand-transition brand-hover-lift">
                <Image
                  src={feature?.image}
                  alt={`${feature?.publication} feature article`}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-primary">{feature?.publication}</span>
                    <span className="text-xs text-muted-foreground">{feature?.date}</span>
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-3">{feature?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-4">{feature?.excerpt}</p>
                  <a
                    href={feature?.link}
                    className="inline-flex items-center space-x-2 text-accent hover:text-accent/80 brand-transition"
                  >
                    <span className="text-sm font-semibold">Read Article</span>
                    <Icon name="ExternalLink" size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-4">Trusted by Industry Leaders</h3>
            <p className="text-white/90 max-w-2xl mx-auto">
              Our recognition comes from the trust and success of our clients across various industries
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Security & Compliance</h4>
              <p className="text-white/80">ISO 27001 compliant with enterprise-grade security standards</p>
            </div>

            <div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Client Partnerships</h4>
              <p className="text-white/80">Long-term relationships with 95% client retention rate</p>
            </div>

            <div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="TrendingUp" size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Proven Results</h4>
              <p className="text-white/80">Average 250% improvement in client business metrics</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecognitionSection;